import { ref, computed, type Ref } from 'vue'
import type { SupabaseClient } from '@supabase/supabase-js'
import { getLanguageName } from './useI18n'
import { featureOff } from './useServiceWorkerSafety'

export interface ContributionTimeframe {
  minutes: number
  phrases: number
  speakers: number
}

export interface ContributionData {
  global: {
    today: ContributionTimeframe
    days7: ContributionTimeframe
    days30: ContributionTimeframe
    allTime: ContributionTimeframe
  }
  user: {
    today: { minutes: number; phrases: number }
    days7: { minutes: number; phrases: number }
    days30: { minutes: number; phrases: number }
    allTime: { minutes: number; phrases: number }
  }
  targetLanguage: string
  languageName: string
}

function emptyTimeframe(): ContributionTimeframe {
  return { minutes: 0, phrases: 0, speakers: 0 }
}

export function useContribution(client: Ref<SupabaseClient | null>) {
  const data = ref<ContributionData | null>(null)
  const isLoading = ref(false)
  const localPhraseIncrement = ref(0)
  const localMinuteIncrement = ref(0)

  let fetchVersion = 0

  async function fetch(courseId: string, learnerId?: string) {
    if (!client.value) return
    // runtime kill-switch (app_config.kill_switches.contribution_totals=false, via /api/sw-config): no community reads
    if (featureOff('contribution_totals')) return
    const version = ++fetchVersion
    isLoading.value = true

    // courseId.split('_for_')[0] is NOT always the target language — course
    // codes for regional variants (spa_mx_for_eng, fra_ca_for_eng,
    // por_br_for_eng) carry a qualifier before '_for_' that isn't a real
    // language code, so the naive split produced 'spa_mx' and getLanguageName
    // fell through to the raw uppercased string ("SPA_MX") in the contribution
    // modal, plus every community query below silently matched zero rows.
    // The course's own target_lang column is the source of truth.
    let targetLang = courseId.split('_for_')[0]
    try {
      const { data: courseRow } = await client.value
        .from('courses')
        .select('target_lang')
        .eq('course_code', courseId)
        .maybeSingle()
      if (courseRow?.target_lang) targetLang = courseRow.target_lang
    } catch { /* fall back to the split-derived guess below */ }

    if (version !== fetchVersion) return

    const today = new Date().toISOString().split('T')[0]
    const days7Ago = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0]
    const days30Ago = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0]

    try {
      // User: read directly from learner_speaking_opportunities, the new
      // per-cycle counter table. One row per (learner, course, UTC day);
      // phrases = opportunities, minutes = floor(play_seconds / 60) at
      // read time so per-row rounding can't accumulate.
      //
      // Replaces the prior path that summed sessions.items_practiced and
      // duration_seconds — that one had been silently writing zero for
      // items_practiced because the session-start guard short-circuited.
      const userToday = { minutes: 0, phrases: 0 }
      const user7 = { minutes: 0, phrases: 0 }
      const user30 = { minutes: 0, phrases: 0 }
      const userAll = { minutes: 0, phrases: 0 }
      const seconds = { today: 0, days7: 0, days30: 0, allTime: 0 }

      // Skip the per-user query for guests — `learner_id` is uuid-typed and
      // guest IDs use a `guest-{uuid}` prefix, which Supabase rejects with 400.
      const isGuest = !learnerId || learnerId.startsWith('guest-') || learnerId === 'demo-learner'

      if (learnerId && !isGuest) {
        const { data: oppsRows } = await client.value
          .from('learner_speaking_opportunities')
          .select('day, opportunities, play_seconds')
          .eq('learner_id', learnerId)
          .eq('course_code', courseId)
          // One row per day; without an explicit limit this defaults to ~1000
          // rows, so a very long-running learner's all-time total would silently
          // undercount. 10000 days is ~27 years — safely above any real learner.
          .limit(10000)

        if (oppsRows) {
          for (const r of oppsRows) {
            const day = r.day as string                // 'YYYY-MM-DD'
            const opps = Number(r.opportunities) || 0
            const secs = Number(r.play_seconds) || 0
            const phrases = opps

            seconds.allTime += secs
            userAll.phrases += phrases

            if (day >= days30Ago) {
              seconds.days30 += secs
              user30.phrases += phrases
            }
            if (day >= days7Ago) {
              seconds.days7 += secs
              user7.phrases += phrases
            }
            if (day === today) {
              seconds.today += secs
              userToday.phrases += phrases
            }
          }
        }
      }

      // Round once per timeframe so partial minutes survive daily boundaries.
      userToday.minutes = Math.floor(seconds.today / 60)
      user7.minutes = Math.floor(seconds.days7 / 60)
      user30.minutes = Math.floor(seconds.days30 / 60)
      userAll.minutes = Math.floor(seconds.allTime / 60)

      // Daily server snapshots only: never fall back to population scans.
      const { data: stored, error } = await client.value.rpc('get_community_contribution', { p_target_lang: targetLang })
      if (version !== fetchVersion) return
      if (error) throw error
      const byKey = Object.fromEntries((stored || []).map((r: any) => [r.window_key, r]))
      const pick = (key: string): ContributionTimeframe => {
        const row = byKey[key]
        return row ? { minutes: Number(row.minutes) || 0, phrases: Number(row.phrases) || 0, speakers: Number(row.speakers) || 0 } : emptyTimeframe()
      }

      data.value = {
        global: {
          today: pick('today'),
          days7: pick('days7'),
          days30: pick('days30'),
          allTime: pick('allTime'),
        },
        user: { today: userToday, days7: user7, days30: user30, allTime: userAll },
        targetLanguage: targetLang,
        languageName: getLanguageName(targetLang),
      }

      // Reset local increments on fresh fetch
      localPhraseIncrement.value = 0
      localMinuteIncrement.value = 0
    } catch (e) {
      console.error('[useContribution] fetch error:', e)
    } finally {
      if (version === fetchVersion) isLoading.value = false
    }
  }

  // Call after each cycle completes — no DB round-trip
  function incrementLocal() {
    localPhraseIncrement.value++
  }

  // Computed: today's global total + local increment (for live counter)
  const todayMinutes = computed(() =>
    (data.value?.global.today.minutes || 0) + localMinuteIncrement.value
  )

  const todayPhrases = computed(() =>
    (data.value?.global.today.phrases || 0) + localPhraseIncrement.value
  )

  const todaySpeakers = computed(() =>
    data.value?.global.today.speakers || 0
  )

  const userTodayPhrases = computed(() =>
    (data.value?.user.today.phrases || 0) + localPhraseIncrement.value
  )

  const languageName = computed(() => data.value?.languageName || '')

  return {
    data,
    isLoading,
    fetch,
    incrementLocal,
    todayMinutes,
    todayPhrases,
    todaySpeakers,
    userTodayPhrases,
    languageName,
  }
}
